// modules/animations.js
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

export function initParallax() {

    // ================================================================
    // Parallax =======================================================
    if (!document.querySelector('.media-wrapper')) return;

    gsap.utils.toArray('.media-wrapper').forEach(wrapper => {
        const img = wrapper.querySelector('img');
        if (!img) return; // ensure target exists

        gsap.to(img, {
            y: '-100px', // parallax distance
            ease: 'none',
            scrollTrigger: {
                trigger: wrapper,
                start: 'top bottom',    // when wrapper top hits bottom of viewport
                end: 'bottom top',      // when wrapper bottom hits top of viewport
                scrub: true             // smooth parallax
            }
        });
    });
}



// ================================================================
// Split text (multiple instances)
// ================================================================

export function initSplitTextMasks() {
    const splitSections = [];
    if (!document.querySelector(".split-text")) return;

    gsap.utils.toArray(".split-text").forEach(el => {
        // Function to set up SplitText + ScrollTrigger for one element
        const setupSplit = () => {
            // kill any previous instance
            if (el._split) {
                el._split.revert();
                el._split = null;
            }

            // new SplitText instance
            const split = new SplitText(el, { type: "lines", linesClass: "line" });
            el._split = split; // store reference for later cleanup

            // wrap each line for masking
            split.lines.forEach(line => {
                const wrap = document.createElement("div");
                wrap.classList.add("line-mask");
                wrap.style.overflow = "hidden";
                line.parentNode.insertBefore(wrap, line);
                wrap.appendChild(line);
            });

            // paused tween
            const tween = gsap.from(split.lines, {
                yPercent: 100,
                duration: 0.8,
                ease: "power4.out",
                stagger: 0.15,
                paused: true
            });

            // scrolltrigger
            const st = ScrollTrigger.create({
                trigger: el,
                start: "top 95%",
                once: true,
                invalidateOnRefresh: true,
                onEnter: () => tween.play()
            });

            // play immediately if already visible
            if (st.isActive) {
                requestAnimationFrame(() => tween.play(0));
            }

            splitSections.push({ el, split, tween, st });
        };

        // Initial setup
        setupSplit();

        // Debounced resize handler
        let resizeTimer;
        window.addEventListener("resize", () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                setupSplit(); // re-run setup
                ScrollTrigger.refresh();
            }, 300);
        });
    });
}



// ================================================================
// Fade up =======================================================
export function initFadeUpAnimation() {
    if (!document.querySelector("[data-animation-fadeup]")) return;

    gsap.utils.toArray("[data-animation-fadeup]").forEach(el => {
        // start state
        gsap.set(el, { y: 100, opacity: 0 });

        // paused tween
        const tween = gsap.to(el, {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: "back.out(1.1)",
            overwrite: "auto",
            paused: true
        });

        // trigger that plays it
        const st = ScrollTrigger.create({
            trigger: el,
            start: "top 95%",          // more forgiving than "top bottom-=100"
            once: true,
            invalidateOnRefresh: true,
            onEnter: () => tween.play()
        });

        // if already in view at setup, play immediately
        if (st.isActive) {
            requestAnimationFrame(() => tween.play(0));
        }
    });
}

// ================================================================
// Up =============================================================
export function initUpAnimation() {
    if (!document.querySelector("[data-animation-up]")) return;

    gsap.utils.toArray("[data-animation-up]").forEach(el => {
        // start state
        gsap.set(el, { y: 100 });

        // paused tween
        const tween = gsap.to(el, {
            y: 0,
            duration: 1,
            ease: "back.out(1.1)",
            overwrite: "auto",
            paused: true
        });

        // trigger that plays it
        const st = ScrollTrigger.create({
            trigger: el,
            start: "top 95%",          // more forgiving than "top bottom-=100"
            once: true,
            invalidateOnRefresh: true,
            onEnter: () => tween.play()
        });

        // if already in view at setup, play immediately
        if (st.isActive) {
            requestAnimationFrame(() => tween.play(0));
        }
    });
}

// ================================================================
// Home banner timeline ===========================================
export function initHomeBannerAnimation() {
    if (!document.querySelector(".home-banner")) return;

    const tl = gsap.timeline({
        defaults: { ease: "power4.out", duration: 1, delay: 1 },
    });

    tl.fromTo(
        ".home-banner .bannerContentAnim",
        { y: 100, opacity: 0 },
        {
            y: 0,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.5,
            ease: "back.out(1.1)",
        },
    );

    // Animate the height of the divs
    tl.fromTo(
        ".home-banner__inner__media .media-wrapper",
        { scale: 0, opacity: 0 },
        {
            scale: 1,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.8,
            ease: "power4.out"
        }, "-=1.5"
    );


    tl.fromTo(
        ".home-banner .frame.--top-right",
        { scale: 0, opacity: 1 },
        {
            opacity: 1,
            scale: 1,
            transformOrigin: "top right",
            duration: 1,
            ease: "power4.out"
        },
        "-=1.5"
    );
    tl.fromTo(
        ".home-banner .frame.--bottom-left",
        { scale: 0, opacity: 1 },
        {
            opacity: 1,
            scale: 1,
            transformOrigin: "bottom left",
            duration: 1,
            ease: "power4.out"
        },
        "-=2"
    );

    tl.fromTo(
        ".home-banner .play-btn",
        { opacity: 0 },
        { opacity: 1 },
        "-=1.5"
    );

    // Animate all `.square` elements with stagger and scale
    tl.fromTo(
        ".home-banner .square",
        { scale: 0, opacity: 0 },
        {
            scale: 1,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.8,
            ease: "back.out(1.7)"
        },
        "-=3"
    );
}

// ================================================================
// Content banner timeline ===========================================
export function initContentBannerAnimation() {
    if (!document.querySelector(".content-banner")) return;

    const tl = gsap.timeline({
        defaults: { ease: "power4.out", duration: 1, delay: 1 },
    });
    // Animate the height of the divs
    tl.fromTo(
        ".content-banner__bg",
        { height: 0, opacity: 0 },
        {
            height: '100%',
            opacity: 1,
            transformOrigin: "center center",
            duration: 0.8,
            ease: "power4.out"
        }
    );

    tl.fromTo(
        ".content-banner .frame.--bottom-left",
        {
            opacity: 1,
            translateZ: 0,
            xPercent: -100,
            yPercent: 100
        },
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 0,
            yPercent: 0,
            transformOrigin: "bottom left",
            duration: 1,
            ease: "power4.inOut"
        },
        "-=1.5"
    );
    tl.fromTo(
        ".content-banner .frame.--bottom-right",
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 100,
            yPercent: 100
        },
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 0,
            yPercent: 0,
            transformOrigin: "bottom right",
            duration: 1,
            ease: "power4.inOut"
        },
        "-=2"
    );

    tl.fromTo(
        ".content-banner .bannerContentAnim",
        { y: 100, opacity: 0 },
        {
            y: 0,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.5,
            ease: "back.out(1.1)",
        }, "-=2"
    );

    tl.fromTo(
        ".content-banner .square",
        { scale: 0, opacity: 0 },
        {
            scale: 1,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.8,
            ease: "back.out(1.7)"
        },
        "-=1.5"
    );

}


// ================================================================
// Textbanner timeline ===========================================
export function initTextBannerAnimation() {
    if (!document.querySelector(".text-banner")) return;

    const tl = gsap.timeline({
        defaults: { ease: "power4.out", duration: 1, delay: 1 },
    });
    // Animate the height of the divs
    tl.fromTo(
        ".text-banner__bg",
        { height: 0, opacity: 0 },
        {
            height: '100%',
            opacity: 1,
            transformOrigin: "center center",
            duration: 0.8,
            ease: "power4.out"
        }
    );

    tl.fromTo(
        ".text-banner .frame.--bottom-left",
        {
            opacity: 1,
            translateZ: 0,
            xPercent: -100,
            yPercent: 100
        },
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 0,
            yPercent: 0,
            transformOrigin: "bottom left",
            duration: 1,
            ease: "power4.inOut"
        },
        "-=1.5"
    );
    tl.fromTo(
        ".text-banner .frame.--bottom-right",
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 100,
            yPercent: 100
        },
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 0,
            yPercent: 0,
            transformOrigin: "bottom right",
            duration: 1,
            ease: "power4.inOut"
        },
        "-=2"
    );

    tl.fromTo(
        ".text-banner .bannerContentAnim",
        { y: 100, opacity: 0 },
        {
            y: 0,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.5,
            ease: "back.out(1.1)",
        }, "-=2"
    );

    tl.fromTo(
        ".text-banner .square",
        { scale: 0, opacity: 0 },
        {
            scale: 1,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.8,
            ease: "back.out(1.7)"
        },
        "-=1.5"
    );

}


// ================================================================
//News banner timeline ===========================================
export function initNewsBannerAnimation() {
    if (!document.querySelector(".news-banner")) return;

    const tl = gsap.timeline({
        defaults: { ease: "power4.out", duration: 1, delay: 1 },
    });
    let mm = gsap.matchMedia();

    mm.add(
        {
            // conditions
            isMobile: "(max-width: 600px)",
            isDesktop: "(min-width: 601px)"
        },
        (context) => {
            let { isMobile } = context.conditions;

            tl.fromTo(
                ".news-banner__bg",
                { height: 0, opacity: 0 },
                {
                    height: isMobile ? "350px" : "550px", // 👈 change height based on screen
                    opacity: 1,
                    transformOrigin: "center center",
                    duration: 0.8,
                    ease: "power4.out"
                }
            );
        }
    );

    tl.fromTo(
        ".news-banner .frame.--bottom-left",
        {
            opacity: 1,
            translateZ: 0,
            xPercent: -100,
            yPercent: 100
        },
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 0,
            yPercent: 0,
            transformOrigin: "bottom left",
            duration: 1,
            ease: "power4.inOut"
        },
        "-=1.5"
    );
    tl.fromTo(
        ".news-banner .frame.--bottom-right",
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 100,
            yPercent: 100
        },
        {
            opacity: 1,
            translateZ: 0,
            xPercent: 0,
            yPercent: 0,
            transformOrigin: "bottom right",
            duration: 1,
            ease: "power4.inOut"
        },
        "-=2"
    );

    tl.fromTo(
        ".news-banner__content__media .img-wrapper",
        { yPercent: "-100" },
        {
            yPercent: "0",
            transformOrigin: "center center",
            duration: 1,
            ease: "power4.out"
        }, "-=2"
    );

    tl.fromTo(
        ".news-banner .bannerContentAnim",
        { y: 100, opacity: 0 },
        {
            y: 0,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.5,
            ease: "back.out(1.1)",
        }, "-=2"
    );

    tl.fromTo(
        ".news-banner .square",
        { scale: 0, opacity: 0 },
        {
            scale: 1,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.8,
            ease: "back.out(1.7)"
        },
        "-=1.5"
    );

}

// ================================================================
// college timeline ===============================================
export function initCollageAnimation() {
    if (!document.querySelector(".collage-animation")) return;

    // Create the timeline (paused initially)
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: ".collage-animation",
            start: "top 95%",
            toggleActions: "play none none none",
            once: true,
        },
        defaults: { ease: "power4.out", duration: 1 },
    });

    // Animate the main media wrapper
    tl.fromTo(
        ".collage-animation .media-wrapper",
        { scale: 0, opacity: 0 },
        {
            scale: 1,
            opacity: 1,
            transformOrigin: "center center",
        }
    );

    // Animate top-right frame
    tl.fromTo(
        ".collage-animation .frame.--top-right",
        { scale: 0, opacity: 1 },
        {
            opacity: 1,
            scale: 1,
            transformOrigin: "top right",
        },
        "-=.5"
    );

    // Animate bottom-left frame
    tl.fromTo(
        ".collage-animation .frame.--bottom-left",
        { scale: 0, opacity: 1 },
        {
            opacity: 1,
            scale: 1,
            transformOrigin: "bottom left",
        },
        "-=1"
    );

    // Animate squares
    tl.fromTo(
        ".collage-animation .square",
        { scale: 0, opacity: 0 },
        {
            scale: 1,
            opacity: 1,
            stagger: 0.2,
            transformOrigin: "center center",
            duration: 0.8,
            ease: "back.out(1.7)",
        },
        "-=1"
    );
}

// ================================================================
// cards animation timeline =======================================
export function initCardAnimations() {
    if (!document.querySelector(".cards")) return;

    const listItems = gsap.utils.toArray(".cards li");

    // Create one ScrollTrigger for the whole section
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: ".cards",
            start: "top 95%",
            toggleActions: "play none none none",
            once: true,
        }
    });

    // Loop through each LI and add its own animation sequence into the same timeline
    listItems.forEach((li, i) => {
        const card = li.querySelector(".card-item");
        const cardicon = li.querySelector("i");
        const cardtitle = li.querySelector(".card-item__content h3");
        const cardcontent = li.querySelector(".card-item .description");
        const cardframe = li.querySelector(".card-item .frame-wrapper");

        // Add your exact existing animations
        const cardTL = gsap.timeline();

        cardTL
            .from(card, {
                delay: 0.25,
                xPercent: -102,
                duration: 1,
                ease: "power4.inOut",
            })


            .from(cardicon, {
                opacity: 0,
                scale: 0,
                duration: 0.5,
                ease: "power4.out"
            })
            .from(cardtitle, {
                opacity: 0,
                y: 20,
                duration: 0.5,
                ease: "power4.out"
            }, "-=0.5")
            .from(cardcontent, {
                opacity: 0,
                y: 20,
                duration: 0.5,
                ease: "power4.out"
            }, "-=0.25")
            .from(cardframe, {
                scale: 0,
                duration: 0.5,
                transformOrigin: "top right",
                ease: "power4.out"
            }, "-=0.25")

            .to(card, {
                duration: 1,
                ease: "power4.inOut",
                borderRadius: 30,
            }, "-=1")

        // Add this card's sequence to the main timeline with a staggered offset
        tl.add(cardTL, i * 0.25); // <— controls stagger between list items
    });
}

// ================================================================
// stats animation timeline =======================================
export function initStatsAnimations() {
    if (!document.querySelector(".stats")) return;

    const statsItems = gsap.utils.toArray(".stats__items li");

    // Create one ScrollTrigger for the whole section
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: ".stats",
            start: "top 95%",
            toggleActions: "play none none none",
            once: true,
        }
    });

    // Loop through each LI and add its own animation sequence into the same timeline
    statsItems.forEach((li, i) => {
        const stat = li.querySelector(".stat-item");
        const statTitle = li.querySelector(".stat-item .large-text");
        const statDescription = li.querySelector(".stat-item .description");

        // Add your exact existing animations
        const statTL = gsap.timeline();

        statTL
            .from(stat, {
                delay: 0.25,
                xPercent: -102,
                duration: 1,
                ease: "power4.out",
                borderRadius: 0,
            })
            .from(statTitle, {
                opacity: 0,
                y: 20,
                duration: 0.5,
                ease: "back.out(1.1)",
            })
            .from(statDescription, {
                opacity: 0,
                y: 20,
                duration: 0.5,
                ease: "back.out(1.1)",
            }, "-=0.25")

            .to(stat, {
                duration: 1,
                ease: "power4.out",
                borderRadius: 30,
            }, "-=1")

        // Add this card's sequence to the main timeline with a staggered offset
        tl.add(statTL, i * 0.15); // <— controls stagger between list items
    });
}


window.addEventListener("load", () => {
    initParallax();
    initSplitTextMasks();
    initContentBannerAnimation();
    initTextBannerAnimation();
    initNewsBannerAnimation();
    initHomeBannerAnimation();
    initCollageAnimation();
    initFadeUpAnimation();
    initUpAnimation();
    initCardAnimations();
    initStatsAnimations();

    ScrollTrigger.refresh();
});
